import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MappingsApiAdapter } from '../adapters/MappingsApiAdapter';
import {
  MappingFilters,
  CreateMappingPayload,
  UpdateMappingPayload,
  IncidentType,
  CourierIncidentKind,
} from '../models/Mapping';

export interface AdvancedFilter {
  column: string;
  operator: string;
  value: any;
}

export interface UseMappingsFilters extends MappingFilters {
  advanced_filters?: {
    filters: AdvancedFilter[];
    logic: 'AND' | 'OR';
  };
}

/**
 * Hook para obtener el listado de mapeos con paginación infinita
 */
export const useMappings = (filters: UseMappingsFilters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MappingsApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['mappings', filters],
    queryFn: ({ pageParam = 1 }) => {
      return apiAdapter.getMappings({
        ...filters,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.meta?.currentPage ?? 1;
      const lastPageNum = lastPage?.meta?.lastPage ?? 1;
      return currentPage < lastPageNum ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 1 * 60 * 1000, // 1 minuto
    gcTime: 5 * 60 * 1000, // 5 minutos
    placeholderData: (previousData) => previousData,
  });

  const mappings = data?.pages.flatMap((page) => page?.data ?? []) ?? [];

  // Obtener el total del API (siempre será el mismo en todas las páginas)
  const totalFromApi = data?.pages?.[0]?.meta?.total ?? 0;

  return {
    mappings,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total: totalFromApi,
  };
};

/**
 * Hook para obtener los tipos de incidencias internos
 */
export const useIncidentTypes = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MappingsApiAdapter(httpClient);

  return useQuery({
    queryKey: ['incident-types'],
    queryFn: () => apiAdapter.getIncidentTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutos (catálogo cambia poco)
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Hook para obtener los tipos de incidencias de un courier
 */
export const useCourierIncidentKinds = (courierId: number | null) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MappingsApiAdapter(httpClient);

  return useQuery({
    queryKey: ['courier-incident-kinds', courierId],
    queryFn: () => apiAdapter.getCourierIncidentKinds(courierId!),
    enabled: !!courierId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/**
 * Hook para obtener la lista de couriers disponibles
 */
export interface Courier {
  id: number;
  name: string;
  image_hash?: string | null;
}

export const useCouriers = () => {
  const httpClient = createHttpClient(apiClientWH);

  return useQuery<Courier[]>({
    queryKey: ['couriers'],
    queryFn: async () => {
      const raw = await httpClient.get<any>('/couriers');
      const list = Array.isArray(raw) ? raw : raw?.data ?? [];
      return list.map((courier: any) => ({
        id: typeof courier.id === 'string' ? parseInt(courier.id, 10) : courier.id,
        name: courier.name,
        image_hash: courier.image_hash || courier.image_url || null,
      }));
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
};

/**
 * Hook para crear un mapeo
 */
export const useCreateMapping = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MappingsApiAdapter(httpClient);

  return useMutation({
    mutationFn: (payload: CreateMappingPayload) =>
      apiAdapter.createMapping(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
    },
  });
};

/**
 * Hook para actualizar un mapeo
 */
export const useUpdateMapping = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new MappingsApiAdapter(httpClient);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateMappingPayload;
    }) => apiAdapter.updateMapping(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
    },
  });
};
